import * as React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import useBundleTranslation from 'i18n';
import { ContextMenuType, copyTileUrl, openTileUrl, TileInfo } from 'app/home/index.functions';
import { useQuery } from '@tanstack/react-query';
import { BurstCountResponse, homePage } from 'api/home-page';
import { getEditUrl } from 'tools/tools';
import { useNavigate } from 'react-router-dom';
import AsyncPopup from 'components/common/popup/AsyncPopup';
import IconMi from 'components/common/icon/IconMi';
import { useHomeContext } from 'app/home/context';
import { UserAuth } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import { useIsAdminOrHasPrivilege } from 'hooks/useUserPrivilege';
import { Box } from '@mui/material';
import styles from './ContextMenu.styles';
import { useEffect, useRef } from 'react';

export default function ContextMenu({
    contextMenu,
    setContextMenu,
    tile,
    setContextMenuTile,
}: {
    contextMenu: ContextMenuType;
    setContextMenu: (contextMenu: ContextMenuType) => void;
    tile: TileInfo | null;
    setContextMenuTile: (tile: TileInfo | null) => void;
}) {
    const ctx = useHomeContext();
    const [setAccessDeniedPopupTile] = [ctx.setAccessDeniedPopupTile];

    const { t } = useBundleTranslation('app/home');
    const userAuth: UserAuth = useAppSelector((state) => state.auth);

    const [showBurstsPopup, setShowBurstsPopup] = React.useState(false);
    const navigate = useNavigate();
    const menuRef = useRef<HTMLDivElement | null>(null);
    const { data: burstCountData } = useQuery<BurstCountResponse, Error>({
        queryKey: [`tile-${tile?.user_dashboard_element_instance_id}-included-in-bursts`],
        queryFn: () => {
            return homePage.getBurstsCount(tile?.element_id ?? 0, tile?.segment_value_id ?? 0);
        },
    });

    const handleClose = () => {
        setContextMenu(null);
        setContextMenuTile(null);
    };

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!tile) return;

        navigate(getEditUrl(tile.element_type || '') + tile.element_id);
        handleClose();
        return false;
    };

    const handleView = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!tile) return;

        openTileUrl(tile, navigate);
        handleClose();
        return false;
    };

    const handleCopyUrl = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!tile) return;

        copyTileUrl(tile);

        handleClose();
        return false;
    };

    const handleNewBurst = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!tile) return;

        homePage.handleNewBurst(tile?.element_id ?? 0, tile?.segment_value_id ?? 0).then((response) => {
            if (response.redirectUrl) navigate(response.redirectUrl);
        });

        handleClose();
        return false;
    };

    const handleShowBursts = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!tile) return;

        setShowBurstsPopup(true);
        return false;
    };

    const handleRequestAccess = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!tile) return;

        setAccessDeniedPopupTile(tile);
        handleClose();
        return false;
    };

    const canAddBurst = useIsAdminOrHasPrivilege('PRIV_CREATE_EDIT_BURSTS');

    useEffect(() => {
        const handleDocumentClick = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                handleClose();
            }
        };
        document.addEventListener('mousedown', handleDocumentClick);
        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
        };
    }, []);

    return (
        tile &&
        (showBurstsPopup ? (
            <AsyncPopup
                popupSettings={{
                    title: t('included_in_bursts'),
                    textOK: t('close'),
                    maxWidth: 'popupXl',
                    noCancel: true,
                }}
                onHide={() => {
                    setShowBurstsPopup(false);
                }}
                onConfirm={() => {
                    setShowBurstsPopup(false);
                }}
                settingsUrl={`/data/notification/burst/included/index?element_id=${tile?.element_id}&segment_value_id=${tile?.segment_value_id}`}
            />
        ) : (
            <Menu
                open={contextMenu !== null}
                ref={menuRef}
                onClose={handleClose}
                anchorReference="anchorPosition"
                PopoverClasses={{
                    root: 'custom-popover-root',
                }}
                anchorPosition={
                    contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
                }
                sx={styles.menuHolder}
            >
                {'Y' === tile.has_access ? (
                    <>
                        {burstCountData && burstCountData.data ? (
                            <MenuItem onClick={handleShowBursts} sx={styles.menuItem}>
                                <Box sx={styles.menuItemLabel}>
                                    {t('included_in_bursts', {
                                        count:
                                            burstCountData && burstCountData.data && burstCountData.data.count
                                                ? burstCountData.data.count
                                                : 0,
                                    })}
                                </Box>
                            </MenuItem>
                        ) : (
                            <MenuItem>
                                <Box sx={styles.menuItemIcon}>
                                    <IconMi icon={'loader'} fontSize={'16'} className={'anim-spin'} />
                                </Box>
                                <Box sx={styles.menuItemLabel}>{t('loading___')}</Box>
                            </MenuItem>
                        )}
                        <Divider sx={{ my: '0 !important' }} />
                        <MenuItem onClick={handleCopyUrl} sx={styles.menuItem}>
                            <Box sx={styles.menuItemIcon}>
                                <IconMi icon={'link'} fontSize={'16'} />
                            </Box>
                            <Box sx={styles.menuItemLabel}>{t('copy_url')}</Box>
                        </MenuItem>
                        {canAddBurst && (
                            <MenuItem onClick={handleNewBurst} sx={styles.menuItem}>
                                <Box sx={styles.menuItemIcon}>
                                    <IconMi icon={'burst'} fontSize={'16'} />
                                </Box>
                                <Box sx={styles.menuItemLabel}>{t('include_in_burst')}</Box>
                            </MenuItem>
                        )}
                        {('Y' == tile.is_tech_editor || userAuth.userInfo?.isAdmin) && (
                            <MenuItem onClick={handleEdit} sx={styles.menuItem}>
                                <Box sx={styles.menuItemIcon}>
                                    <IconMi icon={'gear'} fontSize={'16'} />
                                </Box>
                                <Box sx={styles.menuItemLabel}>{t('edit')}</Box>
                            </MenuItem>
                        )}
                        <MenuItem onClick={handleView} sx={styles.menuItem}>
                            <Box sx={styles.menuItemIcon}>
                                <IconMi icon={'view'} fontSize={'16'} />
                            </Box>
                            <Box sx={styles.menuItemLabel}>{t('view')}</Box>
                        </MenuItem>
                    </>
                ) : (
                    <MenuItem onClick={handleRequestAccess} sx={styles.menuItem}>
                        <Box sx={styles.menuItemIcon}>
                            <IconMi icon={'lock'} fontSize={'16'} />
                        </Box>
                        {t('request_access')}
                    </MenuItem>
                )}
            </Menu>
        ))
    );
}
